import React, { useState } from 'react';
import {
  IAuditoriaRotatividadeProvisoesResultado,
  IItemRotatividadeProvisoes,
} from '../interfaces/IAuditoriaRotatividadeProvisoes';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaRotatividadeProvisoesResultado;
}



const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

const formatarDataMovimento = (value?: string | null): string => {
  if (!value) return 'Sem movimento no período';
  const date = new Date(value);
  if (Number.isNaN(date.getTime()) || date.getFullYear() < 1900) return 'Sem movimento no período';
  return date.toLocaleDateString('pt-BR');
};

// COR-280: drill-down a nível de lançamento — todo AVISO/ERRO precisa mostrar o histórico de
// lançamentos da conta, não só a contagem de dias sem movimento.
const LIMITE_LANCAMENTOS_VISIVEIS = 10;

const BlocoLancamentosRelacionados: React.FC<{ item: IItemRotatividadeProvisoes }> = ({ item }) => {
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const lancamentos = item.lancamentosRelacionados || [];

  if (lancamentos.length === 0) return null;

  const visiveis = mostrarTodos ? lancamentos : lancamentos.slice(0, LIMITE_LANCAMENTOS_VISIVEIS);

  return (
    <div className='mt-2'>
      <strong>Lançamentos Relacionados ({lancamentos.length}):</strong>
      <div className='table-responsive mt-1'>
        <table className='auditoria-table' style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Data</th>
              <th>Nº Lançamento</th>
              <th>Histórico</th>
              <th className='text-end'>Débito</th>
              <th className='text-end'>Crédito</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((lanc, idx) => (
              <tr key={`${lanc.numeroLancamento}-${lanc.data}-${idx}`}>
                <td>{formatDate(lanc.data)}</td>
                <td><code>{lanc.numeroLancamento || '-'}</code></td>
                <td>{lanc.historico}</td>
                <td className='text-end'>{lanc.valorDebito > 0 ? formatCurrency(lanc.valorDebito) : '-'}</td>
                <td className='text-end'>{lanc.valorCredito > 0 ? formatCurrency(lanc.valorCredito) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lancamentos.length > LIMITE_LANCAMENTOS_VISIVEIS && !mostrarTodos && (
        <button
          type='button'
          className='btn btn-link btn-sm p-0 mt-1'
          style={{ fontSize: 12 }}
          onClick={() => setMostrarTodos(true)}
        >
          + {lancamentos.length - LIMITE_LANCAMENTOS_VISIVEIS} lançamento(s) adicional(is)
        </button>
      )}
    </div>
  );
};

const AuditoriaRotatividadeProvisoesDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const pendentes = resultado.provisoesPendentes || [];
  const conciliadas = resultado.provisoesConciliadas || [];
  const avisos = pendentes.filter((item) => item.status === 'AVISO');
  const erros = pendentes.filter((item) => item.status === 'ERRO');

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  const renderTabela = (
    titulo: string,
    itens: IItemRotatividadeProvisoes[],
    corLinha: string,
    mostrarMulta: boolean
  ) => (
    <div className='auditoria-card'>
      <h3 className='mb-0'>{titulo}</h3>

      {itens.length === 0 && (
        <div className='text-muted mt-4'>Nenhuma ocorrência identificada no período.</div>
      )}

      {itens.length > 0 && (
        <div className='table-responsive mt-4'>
          <table className='auditoria-table'>
            <thead>
              <tr>
                <th>Conta</th>
                <th>Tipo de Provisão</th>
                <th>Último Movimento</th>
                <th className='text-center'>Dias sem Manutenção</th>
                <th className='text-end'>Saldo Estagnado</th>
                {mostrarMulta && <th className='text-end'>Multa ECD Potencial</th>}
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {itens.map((item, idx) => {
                const chave = `rp-${item.empresaId}-${item.contaContabil}-${idx}`;
                const expandido = itemExpandido === chave;

                return (
                  <React.Fragment key={chave}>
                    <tr className={`auditoria-row-tributo ${corLinha}`} onClick={() => alternarExpandir(chave)}>
                      <td>
                        <strong>{item.descricaoConta || item.contaContabil}</strong>
                        <div className='text-muted' style={{ fontSize: 11 }}>{item.contaContabil}</div>
                      </td>
                      <td>{item.tipoProvisao}</td>
                      <td>{formatarDataMovimento(item.dataUltimoMovimento)}</td>
                      <td className='text-center'>{item.diasSemMovimento}</td>
                      <td className='text-end'>{formatCurrency(item.saldoEstagnado)}</td>
                      {mostrarMulta && <td className='text-end auditoria-col-highlight'>{formatCurrency(item.valorMultaEcd)}</td>}
                      <td>
                        <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                        <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                      </td>
                    </tr>

                    {expandido && (
                      <tr>
                        <td colSpan={mostrarMulta ? 7 : 6} style={{ background: item.status === 'ERRO' ? '#fff5f5' : '#fffbeb' }}>
                          <div className='py-2 px-3'>
                            <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                            {item.memoriaCalculo && <div><strong>Memória de Cálculo:</strong> {item.memoriaCalculo}</div>}
                            <BlocoLancamentosRelacionados item={item} />
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Governança e Rotatividade de Provisões — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${resultado.impactoFiscalTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Multa ECD (Erro de Manutenção)</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFiscalTotal)}</div>
          {resultado.impactoFiscalTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>Multa ECD potencial — 5% sobre o saldo das provisões sem rotatividade há 60+ dias, limitada a 1% da receita bruta do período (Art. 12, II, Lei 8.218/1991).</div>
          )}
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Saldo Estagnado (Aviso + Erro)</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFinanceiroTotal)}</div>
          <div className='auditoria-kpi-help'>Soma do saldo das provisões sem rotatividade há 31 dias ou mais.</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base legal aplicada à governança de provisões.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Contábil:</strong> {resultado.fundamentacaoContabil}</div>
          <div className='mt-2'><strong>Fiscal:</strong> {resultado.fundamentacaoFiscal}</div>
        </div>
      )}

      {renderTabela('Erro de Manutenção — Sem Rotatividade (>= 60 dias)', erros, 'status-erro', true)}
      {renderTabela('Aviso de Compliance — Sem Rotatividade (31 a 59 dias)', avisos, 'status-aviso', false)}
      {renderTabela('Provisões Conciliadas', conciliadas, '', false)}
    </>
  );
};

export default AuditoriaRotatividadeProvisoesDetalhe;
